import React from 'react';
import {logger} from '../helperFunctions';
import {equals, set} from 'ramda';
import {
  fetchAccount as fetchAccountAPI,
  updateAccount as updateAccountAPI,
  updateAddress as updateAddressAPI
} from '../Api';
import {scope} from '../settings';

const RETRY_DELAY_MS = 500;
const MAX_RETRIES = 5;

const CAN_ADD_PAYMENT_METHOD_POLL_DELAY_MS = 2000;
const MAX_CAN_ADD_PAYMENT_METHOD_POLLS = 3;

const wait = ms => new Promise(resolve => setTimeout(resolve, ms));

const useAccount = ({loading, setLoading, authAndContent}) => {
  const [account, setAccount] = React.useState(null);

  // canAddPaymentMethod can come back empty legitimately, or because it raced
  // other records settling - quietly re-check rather than block or error.
  const pollCanAddPaymentMethod = React.useCallback(
    async (pollCount = 0) => {
      if (pollCount >= MAX_CAN_ADD_PAYMENT_METHOD_POLLS) {
        return;
      }

      await wait(CAN_ADD_PAYMENT_METHOD_POLL_DELAY_MS);
      const ret = await authAndContent
        .getValidToken()
        .then(newToken => fetchAccountAPI(newToken))
        .catch(err => {
          logger('pollCanAddPaymentMethod failed: ', err);
          return null;
        });

      if (ret?.canAddPaymentMethod?.length) {
        setAccount(prev =>
          prev ? {...prev, canAddPaymentMethod: ret.canAddPaymentMethod} : prev
        );
        return;
      }

      return pollCanAddPaymentMethod(pollCount + 1);
    },
    [authAndContent.getValidToken] // eslint-disable-line
  );

  React.useEffect(() => {
    if (authAndContent.token?.accessToken) {
      fetchAccount();
      return;
    }

    setAccount(null);
  }, [authAndContent.token]); // eslint-disable-line

  React.useEffect(() => {
    window.account = account;
  }, [account]);

  const fetchAccount = React.useCallback(
    async (force, count = 0) => {
      if (!force && loading) {
        return;
      }

      if (!loading) {
        setLoading(true);
      }

      return authAndContent.getValidToken().then(newToken =>
        fetchAccountAPI(newToken)
          .then(async ret => {
            if (!ret || ret.errors) {
              // Retry transient failures before surfacing an error.
              if (count < MAX_RETRIES) {
                await wait(RETRY_DELAY_MS);
                return fetchAccount(force, count + 1);
              }
              authAndContent.showError(ret.errors, 'fetchAccount failed');
              logger('fetchAccount failed: ', ret);
              setLoading(false);
              return ret;
            }
            // if account didn't change, check again after 500ms, do this up to 5 times
            if (equals(account, ret) && count < MAX_RETRIES) {
              await wait(RETRY_DELAY_MS);
              return fetchAccount(force, count + 1);
            }
            setAccount(ret);
            setLoading(false);
            if (!ret.canAddPaymentMethod?.length) {
              pollCanAddPaymentMethod();
            }
            return ret;
          })
          .catch(err => {
            logger(err);
            authAndContent.showError(err, 'Unknown Failure');
            setLoading(false);
            return err;
          })
      );
    },
    [authAndContent.getValidToken] // eslint-disable-line
  );

  const updateAccount = async newAccount => {
    setLoading(true);
    return authAndContent.getValidToken().then(newToken =>
      updateAccountAPI(newToken, newAccount).then(ret => {
        setLoading(false);
        if (ret?.errors) {
          return ret;
        }
        return fetchAccount();
      })
    );
  };

  const updateAddress = async account => {
    setLoading(true);
    return updateAddressAPI(scope, account).then(ret => {
      setLoading(false);
      if (ret?.errors) {
        return ret;
      }
      return authAndContent.token ? fetchAccount() : null;
    });
  };

  return {account, setAccount, fetchAccount, updateAccount, updateAddress};
};

export default useAccount;
